// Console : « Activation des clubs · automatique » (D-091, maquette e2), en lecture. Les relances
// partent seules ; ce tableau sert à repérer une étape du guide à améliorer. Admins de la plateforme.
import { consoleActivation, type ActivationRow } from '../lib/guide';
import { OPTIONAL_STEPS, STEP_KEYS } from './steps';
import { html, mount } from '../ui/dom';

/** « à l’import des licenciés » : l'étape où un club bloque. */
const AT: Record<string, string> = {
  club: 'à la création', modules: 'au choix de ce que le club utilise', brand: 'au logo et aux couleurs', import: 'à l’import des licenciés', coachs: 'à l’invitation des coachs',
  bureau: 'à l’invitation du bureau', page: 'à la page du club',
};
/** « inviter les coachs » : l'étape proposée par la dernière relance. */
const TODO: Record<string, string> = {
  club: 'créer le club', modules: 'choisir ce que le club utilise', brand: 'logo et couleurs', import: 'importer les licenciés', coachs: 'inviter les coachs', bureau: 'inviter le reste du bureau',
  page: 'mettre en ligne la page du club',
};

const days = (from: string, to: number) => Math.max(0, Math.floor((to - Date.parse(from)) / 864e5));
const ago = (iso: string, now: number) => { const d = days(iso, now); return d === 0 ? 'aujourd’hui' : d === 1 ? 'hier' : `il y a ${d} jours`; };
const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;

/** La ligne d'un club et la couleur de sa pastille : prêt, en route, bloqué. */
export function activationLine(r: ActivationRow, now = Date.now()): { tone: 'ok' | 'warn' | 'ko'; done: number; total: number; text: string } {
  const total = r.steps.length || STEP_KEYS.bureau.filter(k => !OPTIONAL_STEPS.has(`bureau:${k}`)).length;
  const done = r.steps.filter(s => s.done).length;
  const next = r.steps.find(s => !s.done)?.key;
  if (!next) {
    const ready = r.last_step_at ? days(r.created_at, Date.parse(r.last_step_at)) : days(r.created_at, now);
    return { tone: 'ok', done, total, text: `Prêt en ${plural(Math.max(1, ready), 'jour', 'jours')} · ${plural(r.accounts, 'compte relié', 'comptes reliés')}` };
  }
  const since = r.last_step_at ?? r.created_at;
  const stuck = days(since, now);
  const support = r.support ? `, ${r.support_open ? 'question au support en cours' : 'question au support traitée'}` : '';
  if (r.nudges >= 2 || stuck >= 4) {
    return { tone: 'ko', done, total, text: `Bloqué ${AT[next] ?? 'en route'} depuis ${plural(stuck, 'jour', 'jours')} · ${r.nudges ? plural(r.nudges, 'relance', 'relances') : 'pas encore relancé'}${support}` };
  }
  const nudge = r.nudges && r.last_nudge_at ? `relance envoyée ${ago(r.last_nudge_at, now)} : ${TODO[r.last_nudge_step ?? next] ?? ''}` : `prochaine étape : ${TODO[next] ?? ''}`;
  return { tone: 'warn', done, total, text: `Inscrit ${ago(r.created_at, now)} · ${nudge}${support}` };
}

/** Le bloc de la console ; rien sans club récent (hors démo). */
export async function renderActivation(el: HTMLElement): Promise<void> {
  const rows = await consoleActivation().catch(() => [] as ActivationRow[]);
  if (!rows.length) return;
  mount(el, html`
    <section class="card gd-act-card" data-guide="console-activation">
      <p class="eyebrow">Activation des clubs · automatique</p>
      ${rows.slice(0, 8).map(r => {
        const l = activationLine(r);
        return html`<a class="gd-act-row" href="#/console/club/${r.id}">
          <strong>${r.name}</strong><span class="chip chip-${l.tone}">${l.done} / ${l.total}</span>
          <span class="gd-path-bar" style="--n:${l.total}" aria-hidden="true">${r.steps.map(s => html`<i class="${s.done ? 'on' : ''}"></i>`)}</span>
          <small>${l.text}</small></a>`;
      })}
      <p class="hint">Rien à faire ici : les relances partent seules. Ce tableau sert à repérer une étape du guide à améliorer.</p>
    </section>`);
}
